import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { appointmentApi } from '../services/appointmentApi';
import { socketService } from '../services/socket';
import { sendEmodalLinkStart, linkInstruction } from './emodalLinkHandoff';
import type { Appointment } from '../types/appointment';

/**
 * useFixPlateFlow (B3)
 *
 * Plate-fix is structurally similar to booking but much simpler:
 *   1. POST /appointments/:id/fix-plate-session — backend builds the
 *      eModal edit-mode URL from the appointment's persisted emodal_*
 *      IDs and the task driver's CURRENT truck plate.
 *   2. Send EMODAL_PLATE_FIX_START to the extension with that URL +
 *      userJwt — extension opens the tab and runs the plate-fix content
 *      script which selects the new plate and waits for the receipt.
 *   3. Extension PUTs /appointments/:id/fix-plate-complete with the
 *      receipt's TruckPlate (which we trust over what we filled — see
 *      B3 decision).
 *   4. Backend updates the appointment + emits appointment_updated;
 *      this hook listens and shows a success toast + fires onCompleted.
 *
 * Appointments recorded by hand have no eModal IDs, so step 1 comes back
 * as mode 'link_required' instead. Then the extension takes the operator
 * to eModal to open that appointment, reports its IDs to
 * /appointments/:id/emodal-link, and the appointment_updated event that
 * follows makes this hook re-run step 1 — which now answers 'edit'. From
 * the operator's point of view it is still one click.
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';

/**
 * The extension answers with codes, and every one of them used to be toasted
 * at the operator exactly as written — a dispatcher reading ALREADY_FIXING_PLATE
 * in a red box has no way to know it only means "the last one is still open".
 * Anything not listed here still falls through as-is rather than being hidden.
 */
const HANDOFF_MESSAGES: Record<string, string> = {
  // No longer sent: the extension stopped refusing a second plate fix, each one
  // now runs in its own tab. Kept because an operator on an older build still
  // gets this code, and a raw one reads as a crash.
  ALREADY_FIXING_PLATE:
    'Another plate update is still open on eModal — we just brought that tab forward. '
    + 'Finish it or close the tab, then try this one again.',
  USER_JWT_MISSING: 'Your sign-in expired — sign in again, then retry.',
  INVALID_PAYLOAD: 'This appointment is missing something the plate update needs — reload the page and try again.',
  TAB_OPEN_FAILED: 'Could not open eModal in a new tab — check that Chrome is not blocking pop-ups.',
};

const handoffMessage = (code?: string) =>
  (code && HANDOFF_MESSAGES[code]) || code || 'Extension failed to open eModal';

export interface UseFixPlateFlowOptions {
  /**
   * `appointment` 可空：「票上本来就是这块车牌」那一条也走这里收尾，
   * 而那条路根本没去过 eModal，手上没有新的预约行。调用方要么只用
   * appointmentId，要么自己再拉一次。
   */
  onCompleted?: (payload: { appointmentId: number; appointment?: Appointment }) => void;
  onError?: (payload: { appointmentId: number; reason: string }) => void;
  /**
   * 别报「正在改这一张」。
   *
   * 派完车那一队（usePlateCheckGate）是一张接一张跑的，队伍开头已经说过
   * 「正在改 N 张、会一张张开 eModal」—— 每张再来一条转圈的，一次派 5 只柜
   * 就是 5 条。失败**照报**，quiet 只压成功路上的过程提示。
   */
  quiet?: boolean;
}

function sendEmodalPlateFixStart(payload: {
  sessionId: string;
  appointmentId: number;
  url: string;
  newPlate: string;
  oldPlate: string | null;
  // Lets the assistant tell a loaded eModal edit page from the empty one it
  // renders when the appointment behind the link no longer exists there.
  containerNumber: string | null;
  userJwt: string;
}): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_PLATE_FIX_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

export function useFixPlateFlow(options: UseFixPlateFlowOptions = {}) {
  const { onCompleted, onError, quiet = false } = options;
  const quietRef = useRef(quiet);
  quietRef.current = quiet;
  const activeAppointmentRef = useRef<number | null>(null);
  const activeToastRef = useRef<string | null>(null);
  // Set while the operator is on eModal locating the appointment so we
  // can resume the plate fix the moment the link lands. Cleared on the
  // resume attempt, which is therefore made at most once per click and
  // can't loop if the link somehow didn't take.
  const awaitingLinkRef = useRef<number | null>(null);
  const startRef = useRef<((appointmentId: number) => Promise<{ ok: boolean; error?: string }>) | undefined>(undefined);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  useEffect(() => { onCompletedRef.current = onCompleted; }, [onCompleted]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Listen for the appointment_updated socket event the backend emits on
  // completeFixPlate (it goes through the standard appointment-updated
  // pipe, not a dedicated event). We filter by the active appointmentId.
  useEffect(() => {
    const handle = (raw: unknown) => {
      const data = raw as {
        appointment?: {
          id?: number;
          truck_license_plate?: string | null;
          plate_verified_at?: string | null;
          emodal_truck_visit_id?: number | string | null;
        };
      };
      const a = data.appointment;
      if (!a || !a.id) return;

      // Link landed → pick the plate fix back up where it stopped.
      if (awaitingLinkRef.current === a.id) {
        if (!a.emodal_truck_visit_id) return;
        awaitingLinkRef.current = null;
        if (activeToastRef.current) toast.dismiss(activeToastRef.current);
        activeToastRef.current = null;
        void startRef.current?.(a.id);
        return;
      }

      if (activeAppointmentRef.current !== a.id) return;
      if (!a.plate_verified_at) return; // plate-fix sets this; ignore other updates
      // 改成了不出声：票上的车牌当场就换成新的（Route & Appt 那一行、
      // 派单板那一列都跟着走），再弹一条绿字是把同一件事说第二遍。
      if (activeToastRef.current) toast.dismiss(activeToastRef.current);
      activeAppointmentRef.current = null;
      activeToastRef.current = null;
      onCompletedRef.current?.({ appointmentId: a.id, appointment: a as unknown as Appointment });
    };
    socketService.on('appointment_updated', handle);
    return () => { socketService.off('appointment_updated', handle); };
  }, []);

  // See useRescheduleFlow — eModal opened an appointment it no longer has, so
  // no plate confirmation is coming and the loading toast has nothing to wait
  // for. The backend has already started a sync to re-link this appointment.
  useEffect(() => {
    const handleStale = (raw: unknown) => {
      const data = raw as { appointmentId?: number; intent?: string };
      if (!data?.appointmentId) return;
      if (data.intent !== 'fix_plate') return;
      if (activeAppointmentRef.current !== data.appointmentId
          && awaitingLinkRef.current !== data.appointmentId) return;
      if (activeToastRef.current) toast.dismiss(activeToastRef.current);
      activeToastRef.current = null;
      activeAppointmentRef.current = null;
      awaitingLinkRef.current = null;
      toast.error(
        'This appointment was changed on eModal, so the booking we had is gone. '
        + 'Re-reading eModal now — try Fix Plate again in a minute.'
      );
      onErrorRef.current?.({ appointmentId: data.appointmentId, reason: 'EMODAL_APPT_STALE_LINK' });
    };
    socketService.on('appointment_link_stale', handleStale);
    return () => { socketService.off('appointment_link_stale', handleStale); };
  }, []);

  // Every call site fires this as `void startFixPlate(id)`, so anything we
  // only return is invisible to the operator. The button is now offered on
  // appointments we didn't book — including ones with no driver assigned
  // yet — so the refusals below are reachable in normal use and have to be
  // said out loud.
  const fail = (appointmentId: number, error: string) => {
    toast.error(error);
    onErrorRef.current?.({ appointmentId, reason: error });
    return { ok: false, error };
  };

  // The backend's refusals are the whole point of this flow's 400s — "assign a
  // driver first", "add the eModal ticket number" — and axios throws them away
  // in favour of "Request failed with status code 400". Dig the real message
  // back out; api.ts's interceptor only unwraps `data` on success responses.
  const apiErrorMessage = (err: unknown, fallback: string): string => {
    const res = (err as { response?: { data?: { message?: string } } })?.response;
    return res?.data?.message || (err as Error)?.message || fallback;
  };

  const startFixPlate = useCallback(async (appointmentId: number): Promise<{ ok: boolean; error?: string }> => {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return fail(appointmentId, 'Not signed in');
    if (!window.chrome?.runtime?.sendMessage) {
      return fail(appointmentId, 'EAloom extension not available — install/enable the extension first');
    }

    let session;
    try {
      session = await appointmentApi.createFixPlateSession(appointmentId);
    } catch (err) {
      return fail(appointmentId, apiErrorMessage(err, 'Failed to create plate-fix session'));
    }
    // 票上已经是这块车牌了 —— 不开窗口，当场算完成。
    //
    // 这一队是在点确认那一刻排好的，真正打开 eModal 是后面一张张来的；中间
    // 约票那一下、同一张票另一程的改牌、或者一次同步，都可能已经把它写对了。
    // 不接这一条的话队伍会在这儿干等 15 分钟超时，然后把它算成「没改成」——
    // 屏幕上先说没成、而码头那边早就是对的，两句话互相打脸。
    //
    // 不出声（成功不报），但要把完成信号发出去：队伍靠它往下走，任务详情靠它
    // 重新拉一次预约把 ✅ 画上。
    if (session.mode === 'already_correct') {
      onCompletedRef.current?.({ appointmentId });
      return { ok: true };
    }
    if (!session.newPlate) {
      return fail(appointmentId, 'Task has no driver/truck assigned — assign one first.');
    }

    // Hand-entered appointment: find it on eModal first. The socket
    // handler above resumes this function once the IDs are stored.
    if (session.mode === 'link_required') {
      const toastId = `emodal-link-${appointmentId}-${Date.now()}`;
      activeToastRef.current = toastId;
      awaitingLinkRef.current = appointmentId;
      if (!quietRef.current) toast.loading(linkInstruction(session.search), { id: toastId });

      const linkHandoff = await sendEmodalLinkStart({
        sessionId: session.sessionId,
        appointmentId,
        listUrl: session.listUrl,
        search: session.search,
        intent: 'fix_plate',
        userJwt,
      });
      if (!linkHandoff.ok) {
        toast.dismiss(toastId);
        awaitingLinkRef.current = null;
        activeToastRef.current = null;
        return fail(appointmentId, handoffMessage(linkHandoff.error));
      }
      return { ok: true };
    }

    activeAppointmentRef.current = appointmentId;
    const toastId = `plate-fix-${appointmentId}-${Date.now()}`;
    activeToastRef.current = toastId;
    if (!quietRef.current) toast.loading(`Updating plate to ${session.newPlate}…`, { id: toastId });

    const handoff = await sendEmodalPlateFixStart({
      sessionId: session.sessionId,
      appointmentId,
      url: session.url,
      newPlate: session.newPlate,
      oldPlate: session.oldPlate ?? null,
      containerNumber: session.containerNumber ?? null,
      userJwt,
    });
    if (!handoff.ok) {
      toast.dismiss(toastId);
      activeAppointmentRef.current = null;
      activeToastRef.current = null;
      return fail(appointmentId, handoffMessage(handoff.error));
    }
    return { ok: true };
  }, []);

  // The socket handler resumes through this ref so it doesn't need
  // startFixPlate in its dependency list (which would resubscribe the
  // socket on every render).
  useEffect(() => { startRef.current = startFixPlate; }, [startFixPlate]);

  return { startFixPlate };
}
